import { trText } from '@/i18n/plain';
import React from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

interface Props {
  placeholder?: string;
  value?: string;
  onChangeText?: (v: string) => void;
  onPress?: () => void;
  editable?: boolean;
  onFilterPress?: () => void;
  activeFilterCount?: number;
  /** The keyboard's search key: settle on what is typed. */
  onSubmit?: () => void;
}

/** Read-only mode (onPress, editable=false) is used to launch the full Search screen from
 *  Home/Discover; editable mode is used within the Search screen itself. */
export function SearchBar({ placeholder = trText('Search stylist, shop or service', 'স্টাইলিস্ট, শপ বা সার্ভিস সার্চ করুন'), value, onChangeText, onPress, editable = true, onFilterPress, activeFilterCount, onSubmit }: Props) {
  const { colors } = useAppTheme();
  const Wrapper = editable ? View : Pressable;

  return (
    <View style={styles.row}>
      <Wrapper
        accessibilityRole={editable ? undefined : 'search'}
        onPress={onPress}
        style={[styles.field, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <Ionicons name="search-outline" size={19} color={colors.textTertiary} />
        {editable ? (
          <TextInput
            style={[type.body, styles.input, { color: colors.textPrimary }]}
            placeholder={placeholder}
            placeholderTextColor={colors.textTertiary}
            value={value}
            onChangeText={onChangeText}
            maxLength={100}
            returnKeyType="search"
            onSubmitEditing={onSubmit}
          />
        ) : (
          <Text style={[type.body, styles.placeholder, { color: colors.textTertiary }]}>{placeholder}</Text>
        )}
      </Wrapper>
      {onFilterPress && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Filters"
          onPress={onFilterPress}
          style={[styles.filterBtn, { backgroundColor: colors.accentFill }]}>
          <Ionicons name="options-outline" size={19} color={colors.textOnAccent} />
          {!!activeFilterCount && (
            <View style={[styles.filterDot, { backgroundColor: colors.accentFill }]}>
              <Text style={[styles.filterDotText, { color: colors.textOnAccent }]}>{activeFilterCount}</Text>
            </View>
          )}
        </Pressable>
      )}
    </View>
  );
}

const styles = ScaledSheet.create({
  row: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  field: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    height: 48,
  },
  input: { flex: 1, padding: 0 },
  placeholder: { flex: 1 },
  filterBtn: {
    width: 48,
    height: 48,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterDot: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  filterDotText: { fontSize: 10, fontFamily: 'Manrope_700Bold' },
});
